import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Card, Tag, Row, Col, Upload, message, Image, Collapse, Spin } from 'antd';
import { PlusOutlined, UploadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import CustomLayout from '../layouts/Customlayout';
import config from '../config';
import { useTranslation } from 'react-i18next';

const { Panel } = Collapse;

export default function TicketSystem() {
  const { t } = useTranslation();
  const integrationTranslations = t("integration");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Token ${localStorage.getItem('token')}`,
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.apiUrl}user/tickets/`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch tickets');
      const data = await response.json();
      setTickets(data);
    } catch (error) {
      message.error(t("helpTab.errorFetchingTickets") + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const postTicket = async (values) => {
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('description', values.description);
    formData.append('status', 'Open');
    if (values.image && values.image.file) {
      formData.append('image', values.image.file.originFileObj);
    }

    try {
      const response = await fetch(`${config.apiUrl}user/tickets/`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to create ticket');
      const ticket = await response.json();
      setTickets((prev) => [...prev, ticket]);
      message.success(t("helpTab.successCreateTicket"));
    } catch (error) {
      message.error(t("helpTab.errorCreateTicket") + error.message);
    }
  };

  const handleFinish = (values) => {
    postTicket(values);
    setIsModalVisible(false);
  };

  const handleFileChange = (info) => {
    if (info.file.status !== 'uploading') {
      form.setFieldsValue({ image: info });
    }
  };

  const statusColors = {
    Open: '#FF6B6B',
    'In Progress': '#6C5CE7',
    Solved: '#28A745',
  };

  const tagStyle = (color) => ({
    color,
    border: `1px solid ${color}`,
    backgroundColor: 'transparent',
  });

  const UserInfo = JSON.parse(localStorage.getItem('UserObject'));
  const isShopifyUser = UserInfo?.mode === 'shopify';

  const integrationVideos = [
    {
      title: t("helpTab.zapierIntegrationTitle"),
      description: t("helpTab.zapierIntegrationDesc"),
      videoUrl: 'https://www.youtube.com/embed/k0Oaw38PnBg',
    },
    {
      title: t("helpTab.whatsappIntegrationTitle"),
      description: t("helpTab.whatsappIntegrationDesc"),
      videoUrl: 'https://www.youtube.com/embed/cbOhTTX-12w',
    },
    ...(isShopifyUser
      ? [
          {
            title: t("helpTab.shopifyIntegrationTitle"),
            description: t("helpTab.shopifyIntegrationDesc"),
            videoUrl: 'https://www.youtube.com/embed/example9',
          },
        ]
      : [
          {
            title: t("helpTab.websiteIntegrationTitle"),
            description: t("helpTab.websiteIntegrationDesc"),
            videoUrl: 'https://www.youtube.com/embed/YroFFkZRDx8',
          },
          {
            title: t("helpTab.wordpressIntegrationTitle"),
            description: t("helpTab.wordpressIntegrationDesc"),
            videoUrl: 'https://www.youtube.com/embed/86lP7tXuwD4',
          },
        ]),
  ];

  const helpVideos = [
    {
      title: t("helpTab.customizationTitle"),
      description: t("helpTab.customizationDesc"),
      videoUrl: 'https://www.youtube.com/embed/mfiRVlhjh1s',
    },
    {
      title: t("helpTab.leadsTitle"),
      description: t("helpTab.leadsDesc"),
      videoUrl: 'https://www.youtube.com/embed/m_66IvkTRFA',
    },
    {
      group: t("helpTab.integrationsGroup"),
      items: integrationVideos,
    },
    {
      title: t("helpTab.trainingTitle"),
      description: t("helpTab.trainingDesc"),
      videoUrl: 'https://www.youtube.com/embed/8dXMk-GdJ88',
    },
    {
      title: t("helpTab.teamTitle"),
      description: t("helpTab.teamDesc"),
      videoUrl: 'https://www.youtube.com/embed/LXgd4h-sVyk',
    },
    {
      title: t("helpTab.accountSettingsTitle"),
      description: t("helpTab.accountSettingsDesc"),
      videoUrl: 'https://www.youtube.com/embed/example9',
    },
  ];

  return (
    <CustomLayout>
      <div className="ticket-system-container">
        <div className='help-section'>
          <div className="ticket-system-header">
            <h2 className="title font-bold">{t("helpTab.ticketTitle")}</h2>
            <Button type="primary" icon={<PlusOutlined />} className='bg-[#9E41FF]' onClick={showModal}>
              {t("helpTab.createTicket")}
            </Button>
          </div>

          <Row gutter={[16, 16]} className="ticket-list">
            {loading ? (
              <div className='flex justify-center items-center'>
                <Spin size='small' />
              </div>
            ) : (
              tickets.map((ticket) => (
                <Col xs={24} sm={12} md={8} lg={6} key={ticket.id}>
                  <Card
                    hoverable
                    style={{
                      borderTop: `5px solid ${statusColors[ticket.status]}`,
                      height: '100%',
                    }}
                    className="ticket-card"
                  >
                    <div className="ticket-card-content">
                      <h3 className="ticket-title">{ticket.title}</h3>
                      <Tag style={tagStyle(statusColors[ticket.status])}>{ticket.status}</Tag>
                    </div>
                    <p className="ticket-description">{ticket.description}</p>
                    {ticket.image && (
                      <div className="ticket-image">
                        <Image
                          width="100%"
                          src={ticket.image}
                          placeholder={<Image preview={false} src={ticket.image} width="100%" />}
                        />
                      </div>
                    )}
                  </Card>
                </Col>
              ))
            )}
          </Row>

          <Modal title={t("helpTab.modalTitle")} open={isModalVisible} onCancel={handleCancel} footer={null}>
            <Form form={form} layout="vertical" onFinish={handleFinish}>
              <Form.Item name="title" label={t("helpTab.title")} rules={[{ required: true, message: t("helpTab.titleError") }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label={t("helpTab.description")} rules={[{ required: true, message: t("helpTab.descriptionError") }]}>
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item name="image" label={t("helpTab.uploadImage")}>
                <Upload listType="picture" maxCount={1} onChange={handleFileChange} beforeUpload={() => false}>
                  <Button icon={<UploadOutlined />}>{t("helpTab.uploadBtn")}</Button>
                </Upload>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">{t("helpTab.submit")}</Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>

        {/* Help Section */}
        <div className="help-section mt-5">
          <h2 className="title font-bold">{t("helpTab.helpSectionTitle")}</h2>
          <Collapse accordion>
            {helpVideos.map((video, index) =>
              video.group ? (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PlayCircleOutlined style={{ fontSize: '20px', marginRight: '10px', color: '#6C5CE7' }} />
                      {video.group}
                    </div>
                  }
                  key={`group-${index}`}
                >
                  {video.items.map((item, subIndex) => (
                    <div key={`item-${subIndex}`} className="integration-item">
                      <h3 style={{ fontWeight: 'bold', marginBottom: '5px' }}>{item.title}</h3>
                      <p>{item.description}</p>
                      <iframe
                        width="100%"
                        height="315"
                        src={item.videoUrl}
                        title={item.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                      <hr style={{ margin: '20px 0' }} />
                    </div>
                  ))}
                </Panel>
              ) : (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PlayCircleOutlined style={{ fontSize: '20px', marginRight: '10px', color: '#6C5CE7' }} />
                      {video.title}
                    </div>
                  }
                  key={index}
                >
                  <p>{video.description}</p>
                  <iframe
                    width="100%"
                    height="315"
                    src={video.videoUrl}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </Panel>
              )
            )}
          </Collapse>
        </div>
      </div>
    </CustomLayout>
  );
}
